import React from 'react';

const WWIPage = () => {
  const timeline = [
    { year: '1914', event: 'Assassination of Archduke Franz Ferdinand', description: 'The spark that ignited the war - Gavrilo Princip assassinates the heir to the Austro-Hungarian throne in Sarajevo.' },
    { year: '1914', event: 'War Declarations Begin', description: 'Austria-Hungary declares war on Serbia, triggering a chain reaction of alliances across Europe.' },
    { year: '1914', event: 'Battle of the Marne', description: 'The First Battle of the Marne halts the German advance into France and begins trench warfare.' },
    { year: '1915', event: 'Trench Warfare Intensifies', description: 'The Western Front becomes a brutal stalemate with millions trapped in trenches.' },
    { year: '1916', event: 'Battle of Verdun', description: 'One of the longest and costliest battles, symbolizing the war\'s futility and devastation.' },
    { year: '1916', event: 'Battle of the Somme', description: 'Over one million casualties in one of history\'s bloodiest battles.' },
    { year: '1917', event: 'United States Enters the War', description: 'American entry tips the balance, providing fresh troops and resources to the Allies.' },
    { year: '1917', event: 'Russian Revolution', description: 'Russia withdraws from the war following the Bolshevik Revolution.' },
    { year: '1918', event: 'German Spring Offensive', description: 'Germany\'s last major offensive fails to break Allied lines.' },
    { year: '1918', event: 'Armistice', description: 'The war ends on November 11, 1918, at 11:00 AM - the eleventh hour of the eleventh day of the eleventh month.' }
  ];

  const keyFacts = [
    { icon: '☠️', title: 'Casualties', fact: 'Over 17 million deaths, including 9 million soldiers and 8 million civilians' },
    { icon: '🌍', title: 'Global Conflict', fact: 'Involved 30 nations and fought across Europe, Africa, Middle East, and Asia' },
    { icon: '💣', title: 'New Weapons', fact: 'First use of tanks, poison gas, aircraft, and submarines in warfare' },
    { icon: '📄', title: 'Treaty of Versailles', fact: 'Officially ended the war in 1919, imposing harsh penalties on Germany' },
    { icon: '🏛️', title: 'Empire Collapse', fact: 'Led to the fall of four major empires: German, Austro-Hungarian, Ottoman, and Russian' },
    { icon: '🕊️', title: 'Duration', fact: 'Lasted 4 years, 3 months, and 1 week (July 28, 1914 - November 11, 1918)' }
  ];

  return (
    <div className="min-h-screen bg-stone-50" data-testid="wwi-page">
      {/* Hero Section */}
      <div 
        className="relative h-96 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1574088768814-c71125083959?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHw0fHx3b3JsZCUyMHdhciUyMHRyZW5jaGVzfGVufDB8fHx8MTc3NjE1Nzk2MHww&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="hero-overlay absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-6">
            <h1 className="text-6xl md:text-7xl font-bold mb-4 text-amber-400" data-testid="page-title">
              World War I
            </h1>
            <p className="text-2xl text-amber-100 font-semibold">1914 - 1918</p>
            <p className="text-xl text-amber-200 mt-4 italic">"The War to End All Wars"</p>
          </div>
        </div>
      </div>

      {/* Overview Section */}
      <div className="container mx-auto px-6 py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-stone-800 mb-6" data-testid="overview-title">Overview</h2>
          <div className="section-divider"></div>
          <p className="text-xl text-stone-700 leading-relaxed mb-6">
            World War I, also known as the Great War, was one of the deadliest conflicts in human history. It began in 1914 following the assassination of Archduke Franz Ferdinand of Austria and lasted until 1918. The war pitted the Central Powers—primarily Germany, Austria-Hungary, and the Ottoman Empire—against the Allied Powers, which included France, Great Britain, Russia, Italy, Japan, and eventually the United States.
          </p>
          <p className="text-xl text-stone-700 leading-relaxed mb-6">
            Characterized by brutal trench warfare, the introduction of devastating new military technologies, and unprecedented casualties, WWI fundamentally transformed the political landscape of Europe and the world. The war\'s conclusion led to the collapse of four major empires and set the stage for even greater conflicts in the 20th century.
          </p>
        </div>
      </div>

      {/* Timeline Section */}
      <div className="bg-stone-100 py-16">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-stone-800 mb-12 text-center" data-testid="timeline-title">
            Timeline of Major Events
          </h2>
          <div className="max-w-4xl mx-auto">
            <div className="timeline-line pl-12 space-y-8">
              {timeline.map((item, index) => (
                <div key={index} className="relative" data-testid={`timeline-event-${index}`}>
                  <div className="timeline-dot"></div>
                  <div className="bg-white p-6 rounded-lg shadow-lg">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-2xl font-bold text-amber-600">{item.year}</h3>
                    </div>
                    <h4 className="text-xl font-bold text-stone-800 mb-2">{item.event}</h4>
                    <p className="text-stone-600 text-lg leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Key Facts Section */}
      <div className="container mx-auto px-6 py-16">
        <h2 className="text-4xl font-bold text-stone-800 mb-12 text-center" data-testid="facts-title">
          Key Facts & Figures
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {keyFacts.map((fact, index) => (
            <div 
              key={index} 
              className="bg-white p-6 rounded-lg shadow-lg historical-border hover:shadow-xl transition-shadow"
              data-testid={`fact-card-${index}`}
            >
              <div className="text-4xl mb-4">{fact.icon}</div>
              <h3 className="text-xl font-bold text-amber-600 mb-3">{fact.title}</h3>
              <p className="text-stone-700 text-lg leading-relaxed">{fact.fact}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Causes & Consequences */}
      <div className="bg-stone-100 py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-stone-800 mb-6" data-testid="causes-title">Main Causes</h2>
              <div className="section-divider"></div>
              <ul className="space-y-4 mt-6">
                <li className="flex items-start">
                  <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                  <span className="text-lg text-stone-700">Complex alliance systems that turned a regional conflict into a world war</span>
                </li>
                <li className="flex items-start">
                  <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                  <span className="text-lg text-stone-700">Intense nationalism and imperial competition among European powers</span>
                </li>
                <li className="flex items-start">
                  <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                  <span className="text-lg text-stone-700">Arms race and militarism in the early 20th century</span>
                </li>
                <li className="flex items-start">
                  <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                  <span className="text-lg text-stone-700">Assassination of Archduke Franz Ferdinand as the immediate trigger</span>
                </li>
              </ul>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-stone-800 mb-6" data-testid="consequences-title">Consequences</h2>
              <div className="section-divider"></div>
              <ul className="space-y-4 mt-6">
                <li className="flex items-start">
                  <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                  <span className="text-lg text-stone-700">Collapse of four major empires and redrawing of European map</span>
                </li>
                <li className="flex items-start">
                  <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                  <span className="text-lg text-stone-700">Creation of the League of Nations to prevent future conflicts</span>
                </li>
                <li className="flex items-start">
                  <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                  <span className="text-lg text-stone-700">Economic devastation and the Great Depression</span>
                </li>
                <li className="flex items-start">
                  <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                  <span className="text-lg text-stone-700">Set the stage for World War II through harsh Treaty of Versailles</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WWIPage;